import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSyncLanguage } from "@/hooks/useLanguage";
import JsonLd, { articleJsonLd } from "@/components/feature/JsonLd";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { MAIN_SITE_URL } from "@/mocks/site";
import { PILLAR_ARTICLES } from "@/mocks/articles";

export default function PillarArticle() {
  const { lang: langParam, slug } = useParams();
  const lang = useSyncLanguage(langParam);
  const { t } = useTranslation();

  const article = PILLAR_ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader lang={lang} />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">Article not found</h1>
          <Link to={`/${lang}`} className="mt-6 inline-block rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50">
            {t("cta.back")}
          </Link>
        </main>
        <SiteFooter lang={lang} />
      </div>
    );
  }

  const title = article.title[lang] || article.title.en;
  const summary = article.summary[lang] || article.summary.en;

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={articleJsonLd({
          lang,
          slug: slug || "",
          title,
          description: summary,
          image: article.image,
          dateModified: article.updated,
        })}
      />
      <SiteHeader lang={lang} />

      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to={`/${lang}`} className="transition-colors hover:text-primary-700">
              {t("breadcrumb.home")}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{t("nav.guide")}</span>
          </nav>

          {/* Title */}
          <h1 className="font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{summary}</p>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" /> {article.updated}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-global-line" /> {t("brand.subtag")}
            </span>
          </div>

          {/* Lead */}
          <p className="mt-8 text-lg leading-relaxed text-foreground-800">{article.lead}</p>

          {/* Sections */}
          {article.sections.map((section, idx) => (
            <section key={idx} className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-foreground-700">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{t("faq.title")}</h2>
            <div className="mt-5 space-y-4">
              {article.faq.map((item, i) => (
                <details key={i} className="group rounded-lg border border-background-200 bg-background-50 p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-heading text-base font-semibold text-foreground-900">
                    {item.q}
                    <i className="ri-add-line text-lg text-foreground-500 transition-transform group-open:rotate-45" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Safety note */}
          <section className="mt-10 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-accent-900">
              <i className="ri-shield-check-line" /> {t("safety.title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-accent-800">{article.safety}</p>
          </section>

          {/* CTA with internal link back to home */}
          <section className="mt-10 rounded-lg bg-primary-500 px-6 py-10 text-center">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">{t("cta.title")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
              {t("cta.text")}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to={`/${lang}`}
                className="whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-home-line mr-1.5" /> {t("cta.back")}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                {t("cta.spreadsheet")}
              </a>
            </div>
          </section>
        </article>
      </main>

      <SiteFooter lang={lang} />
    </div>
  );
}